import {FormControl} from '@angular/forms';
import {ImportModuleLookup} from '../../core/definitions/import-module';
import {parseTemplate, TemplatePart} from './value-template';

/**
 * Lookup mode of a mapping row: with `lookupControl` on, the row's value
 * is one column (`lookupLabelControl`) of the row that a lookup - the
 * `LookupDefinition` whose key `lookupRefControl` holds - finds for it,
 * instead of a `{Column}` template.
 */
export interface LookupControls {
  lookupControl: FormControl<boolean>;
  lookupRefControl: FormControl<string | null>;
  lookupLabelControl: FormControl<string | null>;
}

export function createLookupControls(): LookupControls {
  return {
    lookupControl: new FormControl<boolean>(false, {nonNullable: true}),
    lookupRefControl: new FormControl<string | null>(null),
    lookupLabelControl: new FormControl<string | null>(null),
  };
}

export function lookupControlList(controls: LookupControls): FormControl[] {
  return [controls.lookupControl, controls.lookupRefControl, controls.lookupLabelControl];
}

/** The row's single `lookup` part - empty until a lookup and one of its
 * table's columns are picked, and while that lookup is not among
 * `lookupKeys` (see `completeLookupKeys`). */
export function lookupParts(controls: LookupControls, lookupKeys: ReadonlySet<string>): TemplatePart[] {
  const key = controls.lookupRefControl.value;
  const labelColumn = controls.lookupLabelControl.value;
  if (!(key && labelColumn && lookupKeys.has(key))) {
    return [];
  }
  return [{kind: 'lookup', value: key, label_column: labelColumn}];
}

/** Whether `parts` is what `lookupParts` emits: exactly one `lookup` part. */
export function isLookupParts(parts: TemplatePart[]): boolean {
  return parts.length === 1 && parts[0].kind === 'lookup';
}

/**
 * The key options of a lookup, for a table whose key is not the source
 * cell verbatim: a `{Column}` template composing the key, a
 * case-insensitive match, a suffix retried on a miss, and aliases (see
 * `parseAliases`). They reach the lookup only while
 * `lookupKeyOptionsControl` is on.
 */
export interface LookupKeyOptionControls {
  lookupKeyOptionsControl: FormControl<boolean>;
  lookupKeyTemplateControl: FormControl<string>;
  lookupCaseInsensitiveControl: FormControl<boolean>;
  lookupFallbackSuffixControl: FormControl<string>;
  lookupAliasesControl: FormControl<string>;
}

/**
 * One lookup of the page's Kodeoppslag tab: a code in the source's
 * `codeColumnControl` is found in a sibling table (`tableControl`) by that
 * table's `keyColumnControl`. Mapping rows in lookup mode name it by `key`.
 */
export interface LookupDefinition extends LookupKeyOptionControls {
  key: string;
  nameControl: FormControl<string>;
  tableControl: FormControl<string | null>;
  keyColumnControl: FormControl<string | null>;
  codeColumnControl: FormControl<string | null>;
}

export function createLookupDefinition(key: string): LookupDefinition {
  return {
    key,
    nameControl: new FormControl<string>('', {nonNullable: true}),
    tableControl: new FormControl<string | null>(null),
    keyColumnControl: new FormControl<string | null>(null),
    codeColumnControl: new FormControl<string | null>(null),
    lookupKeyOptionsControl: new FormControl<boolean>(false, {nonNullable: true}),
    lookupKeyTemplateControl: new FormControl<string>('', {nonNullable: true}),
    lookupCaseInsensitiveControl: new FormControl<boolean>(false, {nonNullable: true}),
    lookupFallbackSuffixControl: new FormControl<string>('', {nonNullable: true}),
    lookupAliasesControl: new FormControl<string>('', {nonNullable: true}),
  };
}

export function lookupDefinitionControls(lookup: LookupDefinition): FormControl[] {
  return [
    lookup.nameControl, lookup.tableControl, lookup.keyColumnControl, lookup.codeColumnControl,
    lookup.lookupKeyOptionsControl, lookup.lookupKeyTemplateControl, lookup.lookupCaseInsensitiveControl,
    lookup.lookupFallbackSuffixControl, lookup.lookupAliasesControl,
  ];
}

/** Whether the lookup has a table, a key column and a code column - all a
 * run needs of it. */
export function isLookupComplete(lookup: LookupDefinition): boolean {
  return !!(lookup.tableControl.value && lookup.keyColumnControl.value && lookup.codeColumnControl.value);
}

/** The keys of the lookups a run can use (see `isLookupComplete`). */
export function completeLookupKeys(lookups: readonly LookupDefinition[]): Set<string> {
  return new Set(lookups.filter(isLookupComplete).map(lookup => lookup.key));
}

/** The lookup's name for a picker: the one the operator gave it, else its
 * table. Blank for a new lookup with neither. */
export function lookupDisplayName(lookup: LookupDefinition): string {
  return lookup.nameControl.value.trim() || lookup.tableControl.value || '';
}

/** A lookup's name in a picker or a title: its display name, or
 * `TRANS__IMPORT__LOOKUP_NEW` through `translate` for a new one with
 * neither a name nor a table yet. */
export function lookupPickerName(lookup: LookupDefinition, translate: (key: string) => string): string {
  return lookupDisplayName(lookup) || translate('TRANS__IMPORT__LOOKUP_NEW');
}

/** The lookup in the wire shape, or `null` until it is complete.
 * `columnNames` decides which `{Column}` placeholders of the key template
 * are columns. */
export function lookupPayload(lookup: LookupDefinition, columnNames: ReadonlySet<string>): ImportModuleLookup | null {
  if (!isLookupComplete(lookup)) {
    return null;
  }
  const name = lookup.nameControl.value.trim();
  return {
    key: lookup.key,
    ...(name ? {name} : {}),
    table: lookup.tableControl.value!,
    key_column: lookup.keyColumnControl.value!,
    code_column: lookup.codeColumnControl.value!,
    ...(lookup.lookupKeyOptionsControl.value ? lookupKeyOptions(lookup, columnNames) : {}),
  };
}

/** The key options that are actually set. A key template counts only once
 * it names a source column - literal text alone is not a per-row key. */
function lookupKeyOptions(
  controls: LookupKeyOptionControls, columnNames: ReadonlySet<string>,
): Partial<ImportModuleLookup> {
  const options: Partial<ImportModuleLookup> = {};
  const keyParts = parseTemplate(controls.lookupKeyTemplateControl.value, columnNames);
  if (keyParts.some(part => part.kind === 'column')) {
    options.key_parts = keyParts;
  }
  if (controls.lookupCaseInsensitiveControl.value) {
    options.case_insensitive = true;
  }
  if (controls.lookupFallbackSuffixControl.value) {
    options.fallback_suffix = controls.lookupFallbackSuffixControl.value;
  }
  const aliases = parseAliases(controls.lookupAliasesControl.value);
  if (Object.keys(aliases).length) {
    options.aliases = aliases;
  }
  return options;
}

/** Whether a lookup carries any of the key options. */
export function hasLookupKeyOptions(lookup: ImportModuleLookup): boolean {
  return !!(lookup.key_parts?.length || lookup.case_insensitive || lookup.fallback_suffix
    || Object.keys(lookup.aliases ?? {}).length);
}

const ALIAS_SEPARATOR = '=';

/** Aliases as typed: one `code = code it stands for` per line. A line
 * without both sides is skipped; a repeated left side keeps its last line. */
export function parseAliases(text: string): { [code: string]: string } {
  const aliases: { [code: string]: string } = {};
  for (const line of text.split('\n')) {
    const at = line.indexOf(ALIAS_SEPARATOR);
    const from = at === -1 ? '' : line.slice(0, at).trim();
    const to = at === -1 ? '' : line.slice(at + 1).trim();
    if (from && to) {
      aliases[from] = to;
    }
  }
  return aliases;
}

/** The text `parseAliases` reads back into `aliases`. */
export function formatAliases(aliases: { [code: string]: string }): string {
  return Object.entries(aliases).map(([from, to]) => `${from} ${ALIAS_SEPARATOR} ${to}`).join('\n');
}

/** The aliases the lookup applies: none while its key options are off. */
export function activeAliases(controls: LookupKeyOptionControls): { [code: string]: string } {
  return controls.lookupKeyOptionsControl.value ? parseAliases(controls.lookupAliasesControl.value) : {};
}

/** The aliases the lookup applies, keyed by their code as `codeFold`
 * folds it. Where several fold to one key the first listed is kept. */
export function foldedAliases(controls: LookupKeyOptionControls): Map<string, string> {
  const fold = codeFold(controls);
  const aliases = new Map<string, string>();
  for (const [from, to] of Object.entries(activeAliases(controls))) {
    const key = fold(from);
    if (!aliases.has(key)) {
      aliases.set(key, to);
    }
  }
  return aliases;
}

/** Makes `code` stand for `key`, in place of any alias `code` had, and
 * turns the key options on so the alias applies. Options that were off
 * are cleared first - the key template, the case option, the suffix and
 * the aliases typed while they were off - so turning them on applies
 * this alias alone. */
export function setAlias(controls: LookupKeyOptionControls, code: string, key: string): void {
  if (!controls.lookupKeyOptionsControl.value) {
    controls.lookupKeyTemplateControl.setValue('');
    controls.lookupCaseInsensitiveControl.setValue(false);
    controls.lookupFallbackSuffixControl.setValue('');
    controls.lookupAliasesControl.setValue('');
  }
  const lines = linesWithoutAlias(controls, code).filter(line => line.trim());
  controls.lookupAliasesControl.setValue([...lines, `${code} ${ALIAS_SEPARATOR} ${key}`].join('\n'));
  controls.lookupKeyOptionsControl.setValue(true);
}

/** Drops every alias line for `code`; the other lines stay as typed. */
export function removeAlias(controls: LookupKeyOptionControls, code: string): void {
  const text = linesWithoutAlias(controls, code).join('\n');
  if (text !== controls.lookupAliasesControl.value) {
    controls.lookupAliasesControl.setValue(text);
  }
}

function linesWithoutAlias(controls: LookupKeyOptionControls, code: string): string[] {
  const same = codeComparer(controls);
  return controls.lookupAliasesControl.value.split('\n').filter(line => {
    const at = line.indexOf(ALIAS_SEPARATOR);
    return at === -1 || !same(line.slice(0, at).trim(), code);
  });
}

/** A code as the lookup compares it: in lower case for a
 * case-insensitive one, else as it is. */
export function codeFold(controls: LookupKeyOptionControls): (code: string) => string {
  return controls.lookupCaseInsensitiveControl.value ? code => code.toLocaleLowerCase() : code => code;
}

/** Whether two codes are the same code to the lookup (see `codeFold`). */
export function codeComparer(controls: LookupKeyOptionControls): (a: string, b: string) => boolean {
  const fold = codeFold(controls);
  return (a, b) => fold(a) === fold(b);
}

/** Whether the lookup has any key option that reaches its payload. */
export function lookupKeyOptionsInUse(lookup: LookupDefinition, columnNames: ReadonlySet<string>): boolean {
  return lookup.lookupKeyOptionsControl.value && Object.keys(lookupKeyOptions(lookup, columnNames)).length > 0;
}
